import { mapValues } from 'lodash-es'
import { filterAvailableTimes, getTimeFilterDisplayText } from '@myfunnow/web-core/time-filter'

import { FilterOptionKey, TIME_PERIOD_I18N_MAP } from '@/constants/filters'

const filterSettingComposition = props => {
  const { $store: store, $i18n: i18n, $dayjs } = useNuxtApp()
  const isMobile = ref(props.isMobile)
  const filterWindowsStatus = reactive({ chips: [], tags: [] })
  const filterComponentMap = reactive({})
  const filterChips = ref([])
  const filterTags = ref([])
  const dayMode = ref(props.dayMode)
  const baseFilterChips = ref([])
  const allCurrency = computed(() => store.getters['currency/allCurrency'])
  const currency = computed(() => {
    if (allCurrency) {
      const currencyObj = allCurrency.value.find(currency => {
        return currency.id === props.filters.currency_id
      })
      return currencyObj ? currencyObj.code || '' : ''
    } else {
      return ''
    }
  })

  const sort = computed({
    get: () => store.getters['filter/sortType'],
    set: value => store.dispatch('filter/setSortType', { sortType: value }),
  })
  const currentFilters = computed(() => store.getters['filter/filters'])
  const filterTagSettingMap = computed(() => store.getters['filter/filterTags'])
  const selectedNowBtn = computed(() => currentFilters.value.filter_time === FilterOptionKey.NOW)
  const timePeriodLabelMap = computed(() => mapValues(TIME_PERIOD_I18N_MAP, info => i18n.t(info.labelI18n)))

  const getFormatFilterText = tagName => {
    try {
      const filters = currentFilters.value
      let tagText = ''
      switch (tagName) {
        case FilterOptionKey.DATE: {
          let dateText = ''
          if (filters.hasOwnProperty('starttime')) {
            dateText = $dayjs(filters.starttime).format('YYYY/MM/DD')
          }
          tagText = dateText
          break
        }
        case FilterOptionKey.HEAD_COUNT: {
          let limitText = ''
          if (filters.hasOwnProperty('limit')) {
            const headCount = filters.limit
            limitText = i18n.t('all_number_pax', [headCount])
          }
          tagText = limitText
          break
        }
        case FilterOptionKey.PRICE: {
          let priceText = ''
          const priceLimitedInfo = filterComponentMap[FilterOptionKey.PRICE]
          if (filters.hasOwnProperty('min_price') || filters.hasOwnProperty('max_price')) {
            const minPrice = filters.min_price || priceLimitedInfo[0].value
            const maxPrice = filters.max_price || `${priceLimitedInfo[1].value}+`
            priceText = `${currency.value} ${minPrice} - ${maxPrice}`
          }
          tagText = priceText
          break
        }
        case FilterOptionKey.RATING: {
          let ratingText = ''
          if (filters.hasOwnProperty('min_branch_rating')) {
            const maxBranchRating = parseFloat(filters.min_branch_rating).toFixed(1)
            ratingText = `${maxBranchRating}+`
          }
          tagText = ratingText
          break
        }
        case FilterOptionKey.TIME: {
          tagText = getTimeFilterDisplayText({
            selectedTimes: filters.filter_times || [],
            selectedDate: filters.starttime || null,
            periodLabelMap: timePeriodLabelMap.value,
            defaultLabel: i18n.t('all_time'),
            formatExcessLabel: count => i18n.t('filter_time_display', { count }),
          })
          break
        }
      }

      return tagText
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  const findFilterTag = (components, tagName) => {
    const index = (components || []).findIndex(component => {
      return component.name === tagName
    })
    let filter = index >= 0 ? components[index] : {}

    switch (tagName) {
      case FilterOptionKey.DATE:
        filter = index > 0 ? components[index].options[2].components[0] : { options: [] }
        break
      case FilterOptionKey.HEAD_COUNT:
        if (!isMobile) {
          components.options = components.options.map(option => {
            if (option.value) {
              return { ...option, title: i18n.t('number_people', { number: option.value }) }
            }
            return option
          })
        }
        break
    }

    return filter
  }

  const setDataToRenderFilterChips = () => {
    const filters = currentFilters.value
    const filterComponents = props.filters.components
    filterChips.value = []
    baseFilterChips.value.forEach(tagName => {
      const filter = findFilterTag(filterComponents, tagName)
      if (filter) {
        switch (tagName) {
          case FilterOptionKey.DATE: {
            const startDate = filters.starttime || ''
            filterChips.value.push({
              key: FilterOptionKey.DATE,
              title: i18n.t('available_booking_time'),
              value: [startDate],
              text: getFormatFilterText(FilterOptionKey.DATE),
              placeholder: i18n.t('filter_date'),
              keyValue: ['starttime', 'endtime'],
              dayMode: dayMode.value,
              type: 'date-picker',
              icon: 'calendar-icon',
              width: 480,
              activated: !!startDate,
              clearBtn: true,
            })
            break
          }
          case FilterOptionKey.TIME: {
            const filterTimes = filters.filter_times || []
            filterChips.value.push({
              key: FilterOptionKey.TIME,
              value: filterTimes,
              text: getFormatFilterText(FilterOptionKey.TIME),
              keyValue: ['filter_times'],
              type: 'time-picker',
              icon: 'clock',
              width: 480,
              activated: filterTimes.length > 0,
              clearBtn: true,
            })
            break
          }
          case FilterOptionKey.HEAD_COUNT: {
            const headCountValue = filters.limit || ''
            filterComponentMap[FilterOptionKey.HEAD_COUNT] = filter.options
            filterChips.value.push({
              key: FilterOptionKey.HEAD_COUNT,
              title: i18n.t('filter_pax'),
              value: headCountValue,
              text: getFormatFilterText(FilterOptionKey.HEAD_COUNT),
              placeholder: i18n.t('filter_pax'),
              keyValue: ['limit'],
              options: filter.options,
              type: 'list',
              icon: 'member-icon',
              width: 352,
              activated: !!headCountValue,
              clearBtn: false,
            })
            break
          }
          case FilterOptionKey.PRICE: {
            const minPrice = filters.min_price || parseFloat(filter.options?.[0].value)
            const maxPrice = filters.max_price || parseFloat(filter.options?.[1].value)
            const priceRangeValue = [minPrice, maxPrice]
            filterComponentMap[FilterOptionKey.PRICE] = filter.options
            filterChips.value.push({
              key: FilterOptionKey.PRICE,
              title: i18n.t('filter_price'),
              value: priceRangeValue,
              text: getFormatFilterText(FilterOptionKey.PRICE),
              placeholder: i18n.t('filter_price'),
              keyValue: ['min_price', 'max_price'],
              options: filter.options,
              type: 'range-bar',
              icon: 'money-icon',
              width: 400,
              activated: !!filters.min_price || !!filters.max_price,
              clearBtn: true,
            })
            break
          }
          case FilterOptionKey.RATING: {
            const ratingValue = filters.min_branch_rating || ''
            filterComponentMap[FilterOptionKey.RATING] = filter.options
            filterChips.value.push({
              key: FilterOptionKey.RATING,
              title: i18n.t('filter_rate'),
              value: ratingValue,
              text: getFormatFilterText(FilterOptionKey.RATING),
              placeholder: i18n.t('filter_rate'),
              keyValue: ['min_branch_rating'],
              options: filter.options,
              type: 'list',
              icon: 'star-outline',
              width: 352,
              activated: !!ratingValue,
              clearBtn: false,
            })
            break
          }
        }
      }
    })
  }

  const getFormatFilterTagsText = (tagValue, { tags, title }) => {
    const selectedTagNames = tags
      .filter(tag => {
        return tagValue.includes(tag.id)
      })
      .map(tag => {
        return tag.name
      })
    const count = selectedTagNames.length
    return count > 2 ? `${title}(${count})` : count < 1 ? '' : selectedTagNames.join(', ')
  }

  const setDataToRenderFilterTags = () => {
    filterTags.value = []
    props.filters?.filter_tags?.forEach(tagGroup => {
      const tagValue = filterTagSettingMap.value[tagGroup.title] || []
      filterTags.value.push({
        key: tagGroup.title,
        title: tagGroup.title,
        value: tagValue,
        text: getFormatFilterTagsText(tagValue, tagGroup),
        placeholder: tagGroup.title,
        keyValue: ['filter_tags'],
        options: tagGroup.tags,
        type: 'check-box',
        width: 352,
        activated: tagValue.length > 0,
        clearBtn: true,
      })
    })
  }

  const getFilterTagMap = () => {
    const tagGroupsTags = []
    for (const name in filterTagSettingMap.value) {
      const tagsIdText = filterTagSettingMap.value[name].join(',')
      if (tagsIdText) {
        tagGroupsTags.push(`[${tagsIdText}]`)
      }
    }
    const tagGroupsTagsIdText = tagGroupsTags.join(',')
    return { filter_tags: tagGroupsTagsIdText ? `[${tagGroupsTagsIdText}]` : undefined }
  }

  const getFilterParams = () => {
    const filterTagData = getFilterTagMap()
    return { ...currentFilters.value, ...filterTagData }
  }

  const closedMenuValue = ({ type }) => {
    if (type === 'chips') {
      filterWindowsStatus.chips = []
    } else if (type === 'tags') {
      filterWindowsStatus.tags = []
    }
  }

  const cleanupUnavailableFilterTimes = starttime => {
    const currentTimes = currentFilters.value.filter_times || []
    if (currentTimes.length === 0) {
      return
    }

    const availableTimes = filterAvailableTimes(currentTimes, starttime)
    if (availableTimes.length !== currentTimes.length) {
      store.dispatch('filter/setFilters', { filter_times: availableTimes.length > 0 ? availableTimes : false })
    }
  }

  const changeActivatedStatus = (filterTag, activated, params) => {
    switch (filterTag) {
      case FilterOptionKey.NOW: {
        const nowFilter = { filter_time: activated ? FilterOptionKey.NOW : false }
        if (currentFilters.value.starttime) {
          Object.assign(nowFilter, { starttime: false, endtime: false })
        }
        if (currentFilters.value.filter_times) {
          Object.assign(nowFilter, { filter_times: false })
        }
        store.dispatch('filter/setFilters', { ...nowFilter })
        setDataToRenderFilterChips()
        break
      }
      case FilterOptionKey.DATE: {
        store.dispatch('filter/setFilters', { ...params, filter_time: null })
        if (params.starttime) {
          // 當日期變更時，檢查目前選擇的時間是否仍然可用，若不可用則清除時間條件
          cleanupUnavailableFilterTimes(params.starttime)
        }
        setDataToRenderFilterChips()
        break
      }
      case FilterOptionKey.TIME: {
        store.dispatch('filter/setFilters', { ...params, filter_time: null })
        setDataToRenderFilterChips()
        break
      }
      default:
        store.dispatch('filter/setFilters', { ...params })
        setDataToRenderFilterChips()
        break
    }
  }

  const updateChipsCondition = ({ params, filterKey, activated }) => {
    changeActivatedStatus(filterKey, activated, params)
  }

  const updateTagsCondition = ({ params, filterKey, activated }) => {
    store.dispatch('filter/setFilterTags', params)
    // NOTE: 由於 category page 會在 asyncData 中取得 filter_tags 並且設定到 filters 中，所以這邊需要同步更新 filters
    store.dispatch('filter/setFilters', getFilterTagMap())
    setDataToRenderFilterTags()
  }

  const updateFilterCondition = ({ type, params, filterKey, index, activated }) => {
    closedMenuValue({ type: type, index: index })
    type === 'tags' ? updateTagsCondition({ params, filterKey, activated }) : updateChipsCondition({ params, filterKey, activated })
    const filterData = getFilterParams()
    return filterData
  }

  const clickNowFilter = () => {
    const activated = !selectedNowBtn.value
    changeActivatedStatus(FilterOptionKey.NOW, activated)
  }

  const fetchSearchKeywords = regionID => {
    const querys = { regionID: regionID }
    store.dispatch('search/fetchSearchKeywordsOfRegion', querys)
  }

  const initFilters = ({ hasCusNum, hasTimeFilter }) => {
    baseFilterChips.value = []
    baseFilterChips.value.push(FilterOptionKey.PRICE)
    baseFilterChips.value.push(FilterOptionKey.RATING)
    if (hasCusNum) {
      baseFilterChips.value.unshift(FilterOptionKey.HEAD_COUNT)
    }
    if (hasTimeFilter) {
      baseFilterChips.value.unshift(FilterOptionKey.TIME)
      baseFilterChips.value.unshift(FilterOptionKey.DATE)
    }
    setDataToRenderFilterChips()
    setDataToRenderFilterTags()
  }

  const initSearchFilters = () => {
    baseFilterChips.value.push(FilterOptionKey.DATE)
    baseFilterChips.value.push(FilterOptionKey.TIME)
    setDataToRenderFilterChips()
  }

  const initTagFilters = () => {
    baseFilterChips.value = [FilterOptionKey.DATE, FilterOptionKey.TIME]
    setDataToRenderFilterChips()
  }

  return {
    sort,
    currency,
    filterWindowsStatus,
    filterChips,
    filterTags,
    currentFilters,
    selectedNowBtn,
    initFilters,
    initSearchFilters,
    initTagFilters,
    fetchSearchKeywords,
    getFilterParams,
    getFormatFilterText,
    setDataToRenderFilterChips,
    setDataToRenderFilterTags,
    updateChipsCondition,
    updateTagsCondition,
    updateFilterCondition,
    clickNowFilter,
    closedMenuValue,
  }
}

export default filterSettingComposition
